// src/components/features/enrichHistory.ts

import { IBotData, IPerformanceDataPoint } from "../shared/types";


/**
 * Obtiene el timestamp (ms) del inicio del día en UTC dentro de "days" días desde hoy.
 */
function startOfDayUTCPlusDays(days: number): number {
  const now = new Date();
  const year  = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const date  = now.getUTCDate() + days;
  return Date.UTC(year, month, date);
}

type ChartablePerformanceDataPoint = Omit<IPerformanceDataPoint, 'date'> & { date: number };

/**
 * Agrega el histórico de varios bots día a día (UTC),
 * calcula los índices HODL vs Strategy y devuelve
 * ChartablePerformanceDataPoint[] con `date` en UNIX timestamp (ms).
 * Siempre incluye al final un punto “hoy” redondeado al inicio del día siguiente (UTC).
 */
export function processAggregatedHistory(bots: IBotData[]): ChartablePerformanceDataPoint[] {
  if (!bots || bots.length === 0) return [];

  // 1) Agrupar por día “YYYY-MM-DD” en UTC
  const dailyMap = new Map<string, {
    totalCapital: number;
    hodlValue:    number;
    totalPnl:     number;
    totalPrice:   number;
    count:        number;
  }>();

  bots.forEach(bot => {
    const hist = bot.history;
    if (!hist || hist.length === 0) return;
    const initial     = hist[0];
    const initialCap  = initial.totalCapitalUSD;
    const initialPrice= initial.price;
    if (initialCap <= 0 || initialPrice <= 0) return;

    hist.forEach(snap => {
      // toISOString() ya da la fecha en UTC
      const day  = new Date(snap.timestamp).toISOString().slice(0,10);
      const ratio = snap.price / initialPrice;
      const hodl  = initialCap * ratio;

      const agg = dailyMap.get(day) || {
        totalCapital: 0,
        hodlValue:    0,
        totalPnl:     0,
        totalPrice:   0,
        count:        0
      };
      agg.totalCapital += snap.totalCapitalUSD;
      agg.hodlValue    += hodl;
      agg.totalPnl     += snap.pnlUSD ?? 0;
      agg.totalPrice   += snap.price;
      agg.count        += 1;
      dailyMap.set(day, agg);
    });
  });

  // 2) Override de “hoy”
  const todayKey = new Date().toISOString().slice(0,10);
  const override = bots.reduce((acc, bot) => {
    const stats = bot.stats;
    const init  = bot.history?.[0];
    if (!stats || !init) return acc;
    acc.totalCapital += stats.totalCapitalUSD;
    acc.hodlValue    += init.totalCapitalUSD * (stats.currentPrice / init.price);
    acc.totalPnl     += stats.pnlUSD ?? 0;
    acc.totalPrice   += stats.currentPrice;
    acc.count        += 1;
    return acc;
  }, { totalCapital: 0, hodlValue: 0, totalPnl: 0, totalPrice: 0, count: 0 });

  dailyMap.set(todayKey, override);

  // 3) Ordenar y preparar array
  const sorted = Array.from(dailyMap.entries())
    .map(([day, v]) => ({
      date:         day,
      ...v,
      averagePrice: v.totalPrice / v.count
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (sorted.length === 0) return [];

  // 4) Calcular índices acumulados
  let poolIndex  = 1000;
  let tokenIndex = 1000;
  const output: ChartablePerformanceDataPoint[] = [];

  for (let i = 0; i < sorted.length; i++) {
    const cur  = sorted[i];
    const prev = sorted[i - 1];
    const priceChange = prev
      ? (cur.averagePrice - prev.averagePrice) / prev.averagePrice
      : null;
    const pnlChange   = prev
      ? (cur.totalPnl - prev.totalPnl)        / prev.totalCapital
      : null;

    if (priceChange != null) tokenIndex *= 1 + priceChange;
    if (pnlChange   != null) poolIndex  *= 1 + pnlChange;

    output.push({
      date:          new Date(cur.date).getTime(),
      poolIndex:     Number(poolIndex.toFixed(2)),
      tokenIndex:    Number(tokenIndex.toFixed(2)),
      priceChange,
      pnlChange,
      previousPrice: prev?.averagePrice ?? null,
      currentPrice:  cur.averagePrice,
      previousPnl:   prev?.totalPnl ?? null,
      currentPnl:    cur.totalPnl
    });
  }

  // 5) Punto “hoy” al inicio del día siguiente (UTC)
  const last        = output[output.length - 1]!;
  const tomorrowTs  = startOfDayUTCPlusDays(1);
  const aggCount    = override.count;
  const aggAvgPrice = aggCount > 0 ? override.totalPrice / aggCount : null;
  const aggTotalPnl = override.totalPnl;

  const priceChgEnd = last.currentPrice != null && aggAvgPrice != null
    ? (aggAvgPrice - last.currentPrice) / last.currentPrice
    : null;
  const pnlChgEnd   = last.currentPnl != null
    ? (aggTotalPnl  - last.currentPnl)   / override.totalCapital
    : null;

  const finalToken = tokenIndex * (1 + (priceChgEnd ?? 0));
  const finalPool  = poolIndex  * (1 + (pnlChgEnd   ?? 0));

  output.push({
    date:          tomorrowTs,
    poolIndex:     Number(finalPool.toFixed(2)),
    tokenIndex:    Number(finalToken.toFixed(2)),
    priceChange:   priceChgEnd,
    pnlChange:     pnlChgEnd,
    previousPrice: last.currentPrice,
    currentPrice:  aggAvgPrice,
    previousPnl:   last.currentPnl,
    currentPnl:    aggTotalPnl
  });

  return output;
}
